import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import FacilityNetworkBackButton from "./FacilityNetworkBackButton";
import FacilityNetworkFigmaCard from "./FacilityNetworkFigmaCard";
import {
  CAH_DASHBOARD_SUBTITLE,
  CAH_ROSTER_SUBTITLE,
  CAH_TYPE_CODE,
  buildLiveCaHDashboardModel,
  cahToneColor,
  collectLinkedCaHFacilities,
  type CahKpiTile,
  type CahRosterRow,
  type CahTone,
} from "./facilityNetworkCaHDashboardUtils";
import {
  getNetworkCah,
  getNetworkCahForNetwork,
  type CahDashboardData,
  type NetworkDashboardMeta,
} from "@services/networkDashboard";
import { FN } from "./facilityNetworkTheme";

type Props = {
  members: FacilityHierarchyInput[];
  selectedFacilityId: number | null;
  onBack: () => void;
  onOpenFacility?: (facility: MyFacilityListRow) => void;
  onOpenNetworkFacility?: (ccn: string) => void;
  networkId?: number | null;
};

const STATUS_NEUTRAL = "#605e5c";
const VALUE_NEUTRAL = "#323130";

function KpiTile({ tile }: { tile: CahKpiTile }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: { xs: "100%", sm: "calc(50% - 4px)", md: 0 },
        bgcolor: "#fff",
        border: "1px solid #e5e5e5",
        borderRadius: "4px",
        boxShadow:
          "1px 1px 2px rgba(98,102,104,0.05), -1px -1px 2px rgba(98,102,104,0.05)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: STATUS_NEUTRAL,
          lineHeight: "20px",
        }}
      >
        {tile.label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 24,
          fontWeight: 600,
          color: tile.valueTone ? cahToneColor(tile.valueTone) : VALUE_NEUTRAL,
          letterSpacing: FN.figmaTitleTracking,
          lineHeight: "32px",
        }}
      >
        {tile.value}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          color: STATUS_NEUTRAL,
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        {tile.sublabel}
      </Typography>
    </Box>
  );
}

function ToneText({ label, tone }: { label: string; tone: CahTone }) {
  return (
    <Typography
      sx={{
        fontFamily: FN.fontFamily,
        fontSize: 12,
        color: cahToneColor(tone),
        letterSpacing: "0.03px",
        lineHeight: "16px",
      }}
    >
      {label}
    </Typography>
  );
}

function RosterTable({
  rows,
  onOpenFacility,
  onOpenNetworkFacility,
}: {
  rows: CahRosterRow[];
  onOpenFacility?: (facility: MyFacilityListRow) => void;
  onOpenNetworkFacility?: (ccn: string) => void;
}) {
  const headerSx = {
    fontFamily: FN.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: STATUS_NEUTRAL,
    bgcolor: "#fafafa",
    borderColor: "#e5e5e5",
    py: 1,
    px: 1.5,
  };
  const cellSx = {
    fontFamily: FN.fontFamily,
    borderColor: "#e5e5e5",
    py: 1.5,
    px: 1.5,
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, width: 270 }}>Facility</TableCell>
            <TableCell sx={headerSx}>Beds</TableCell>
            <TableCell sx={headerSx}>Operating margin</TableCell>
            <TableCell sx={headerSx}>Swing bed ADC</TableCell>
            <TableCell sx={headerSx}>Last standard survey</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              hover
              onClick={() => {
                if (row.dashboardFacility && onOpenFacility) {
                  onOpenFacility(row.dashboardFacility);
                } else if (row.facilityId == null && onOpenNetworkFacility) {
                  const ccn = row.metaLine.match(/^CCN ([^ ·]+)/)?.[1];
                  if (ccn) onOpenNetworkFacility(ccn);
                }
              }}
              sx={{
                cursor:
                  row.dashboardFacility || onOpenNetworkFacility
                    ? "pointer"
                    : "default",
                "&:hover": { bgcolor: "#f3fdff" },
              }}
            >
              <TableCell sx={cellSx}>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: VALUE_NEUTRAL }}
                >
                  {row.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: STATUS_NEUTRAL }}>
                  {row.metaLine}
                </Typography>
                {row.exclusionLabel ? (
                  <Typography sx={{ fontSize: 11, color: "#8b4409" }}>
                    {row.exclusionLabel}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell sx={cellSx}>
                <Typography sx={{ fontSize: 12, color: "#737373" }}>
                  {row.bedsLabel}
                </Typography>
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText label={row.marginLabel} tone={row.marginTone} />
              </TableCell>
              <TableCell sx={cellSx}>
                <Typography sx={{ fontSize: 12, color: "#737373" }}>
                  {row.swingAdcLabel}
                </Typography>
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText
                  label={row.lastSurveyLabel}
                  tone={row.lastSurveyTone}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

type LiveLoadState =
  | { scopeKey: string; status: "idle" | "loading" }
  | {
      scopeKey: string;
      status: "loaded";
      data: CahDashboardData;
      meta: NetworkDashboardMeta;
    }
  | { scopeKey: string; status: "error"; message: string };

/** Live CAH type dashboard via Rules GET /api/v1/network-dashboard/type/cah */
export default function FacilityNetworkCaHDashboardView({
  members,
  selectedFacilityId,
  onBack,
  onOpenFacility,
  onOpenNetworkFacility,
  networkId = null,
}: Props) {
  const scopeKey =
    networkId != null
      ? `network:${networkId}`
      : selectedFacilityId != null
        ? `facility:${selectedFacilityId}`
        : "none";
  const linked = collectLinkedCaHFacilities(members, selectedFacilityId);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [loadState, setLoadState] = React.useState<LiveLoadState>({
    scopeKey,
    status: "idle",
  });

  React.useEffect(() => {
    if (scopeKey === "none") {
      setLoadState({ scopeKey, status: "idle" });
      return;
    }
    let disposed = false;
    setLoadState({ scopeKey, status: "loading" });
    const request =
      networkId != null
        ? getNetworkCahForNetwork(networkId, {
            limit: 500,
            offset: 0,
            sort: "risk",
            direction: "desc",
          })
        : getNetworkCah(selectedFacilityId!, {
            limit: 500,
            offset: 0,
            sort: "risk",
            direction: "desc",
          });
    void request
      .then((response) => {
        if (!disposed) setLoadState({ scopeKey, status: "loaded", ...response });
      })
      .catch((error) => {
        if (!disposed) {
          setLoadState({
            scopeKey,
            status: "error",
            message:
              error instanceof Error
                ? error.message
                : "Failed to load CAH dashboard.",
          });
        }
      });
    return () => {
      disposed = true;
    };
  }, [networkId, reloadKey, scopeKey, selectedFacilityId]);

  const currentState =
    loadState.scopeKey === scopeKey
      ? loadState
      : ({ scopeKey, status: "loading" } as const);
  const model =
    currentState.status === "loaded"
      ? buildLiveCaHDashboardModel(currentState.data, linked, currentState.meta)
      : null;
  const emptyWarnings =
    currentState.status === "loaded"
      ? [
          ...((currentState.meta.warnings as string[] | undefined) ?? []),
          ...((currentState.meta.data_quality_warnings as string[] | undefined) ??
            []),
        ].filter(Boolean)
      : [];
  const linkedWithoutCcn =
    currentState.status === "loaded" &&
    model != null &&
    model.facilityCount === 0 &&
    Array.isArray(currentState.meta.portfolio_facility_ids) &&
    currentState.meta.portfolio_facility_ids.length > 0
      ? currentState.meta.portfolio_facility_ids.length
      : 0;

  return (
    <Box
      translate="no"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <FacilityNetworkBackButton
        label="Back to Networks Overview"
        onClick={onBack}
        variant="link"
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              letterSpacing: FN.figmaTitleTracking,
              lineHeight: "32px",
            }}
          >
            Critical Access Hospitals
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "24px",
              bgcolor: FN.badgeBg,
              boxShadow: "0px 2px 2px #e9f3f5",
            }}
          >
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.title,
              }}
            >
              {model == null
                ? "Loading…"
                : `${model.facilityCount} Facilit${model.facilityCount === 1 ? "y" : "ies"}`}
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            color: STATUS_NEUTRAL,
            letterSpacing: "0.03px",
          }}
        >
          {CAH_DASHBOARD_SUBTITLE}
        </Typography>
      </Box>

      {scopeKey === "none" ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            Select a facility or network to load CAH dashboard data.
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : currentState.status === "loading" || currentState.status === "idle" ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : currentState.status === "error" ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setReloadKey((k) => k + 1)}
            >
              Retry
            </Button>
          }
        >
          {currentState.message}
        </Alert>
      ) : model && model.facilityCount === 0 ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL, mb: 1 }}>
            {linkedWithoutCcn > 0
              ? `${linkedWithoutCcn} CAH${linkedWithoutCcn === 1 ? "" : "s"} linked, but none have a CMS CCN. Add CCN in each facility profile to load dashboard metrics.`
              : `No ${CAH_TYPE_CODE}s found in this portfolio.`}
          </Typography>
          {emptyWarnings.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {emptyWarnings.map((warning) => (
                <Typography
                  key={warning}
                  component="li"
                  sx={{ fontSize: 12, color: STATUS_NEUTRAL, mb: 0.5 }}
                >
                  {warning}
                </Typography>
              ))}
            </Box>
          ) : null}
        </FacilityNetworkFigmaCard>
      ) : model ? (
        <>
          {emptyWarnings.length > 0 ? (
            <Alert severity="warning" icon={false}>
              <Box>
                {emptyWarnings.map((warning, index) => (
                  <Typography
                    key={`${index}-${warning}`}
                    component="div"
                    sx={{ fontSize: 12 }}
                  >
                    {warning}
                  </Typography>
                ))}
              </Box>
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
            {model.kpis.map((tile) => (
              <KpiTile key={tile.id} tile={tile} />
            ))}
          </Box>

          <FacilityNetworkFigmaCard padding={0} gap={0}>
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e5e5" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: STATUS_NEUTRAL,
                }}
              >
                Facility roster
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: STATUS_NEUTRAL,
                  mt: 0.5,
                }}
              >
                {CAH_ROSTER_SUBTITLE}
              </Typography>
            </Box>
            <RosterTable
              rows={model.roster}
              onOpenFacility={onOpenFacility}
              onOpenNetworkFacility={onOpenNetworkFacility}
            />
          </FacilityNetworkFigmaCard>
        </>
      ) : null}
    </Box>
  );
}
